import React, { useState, useEffect } from 'react';
import { 
  Wallet as WalletIcon, Plus, ArrowDownToLine, ArrowUpFromLine,
  CreditCard, Smartphone, Building, Search, Filter,
  TrendingUp, TrendingDown, Clock, CheckCircle
} from 'lucide-react';
import { useWallet } from '../context/WalletContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Navbar from './Navbar';

const Wallet = () => {
  const { 
    wallet, 
    transactions, 
    withdrawals, 
    loading, 
    fetchTransactions,
    createWithdrawal,
    initiatePayment,
    getTransactionTypeColor,
    getTransactionIcon
  } = useWallet();

  const [activeTab, setActiveTab] = useState('overview');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    bank_account_number: '',
    ifsc_code: '',
    account_holder_name: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const quickAmounts = [100, 500, 1000, 2000, 5000];

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount < 10) {
      setError('Minimum deposit amount is ₹10');
      return;
    }

    setError('');
    setShowDepositModal(false);
    
    await initiatePayment(
      amount,
      (data) => {
        setSuccess(`Successfully deposited ₹${amount}!`);
        setDepositAmount('');
      },
      (error) => {
        setError(error || 'Payment failed');
      }
    );
  };

  const handleWithdrawal = async () => {
    setError('');
    
    const amount = parseFloat(withdrawalForm.amount);
    if (!amount || amount < 100) {
      setError('Minimum withdrawal amount is ₹100');
      return;
    }

    if (amount > wallet?.unutilized_amount) {
      setError('Insufficient unutilized balance');
      return;
    }

    const result = await createWithdrawal(withdrawalForm);
    
    if (result.success) {
      setSuccess('Withdrawal request created successfully!');
      setShowWithdrawModal(false);
      setWithdrawalForm({
        amount: '',
        bank_account_number: '',
        ifsc_code: '',
        account_holder_name: ''
      });
    } else {
      setError(result.error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800">Processing</Badge>;
      case 'failed':
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (!wallet) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-200 text-red-600 rounded-lg">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-200 text-green-600 rounded-lg">
            {success}
          </div>
        )}

        {/* Wallet Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Balance */}
          <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Total Balance</p>
                  <p className="text-3xl font-bold text-gray-900">
                    ₹{wallet.total_amount.toFixed(2)}
                  </p>
                </div>
                <div className="bg-red-600 p-3 rounded-full">
                  <WalletIcon className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unutilized Amount */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Unutilized Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{wallet.unutilized_amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Withdrawable</p>
                </div>
                <ArrowDownToLine className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          {/* Bonus Amount */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Bonus Cash</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{wallet.bonus_amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Non-withdrawable</p>
                </div>
                <div className="text-blue-600">🎁</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button 
            onClick={() => setShowDepositModal(true)}
            className="bg-green-600 hover:bg-green-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Money</span>
          </Button>
          
          <Button 
            onClick={() => setShowWithdrawModal(true)}
            variant="outline"
            className="flex items-center space-x-2"
            disabled={!wallet.unutilized_amount || wallet.unutilized_amount < 100}
          >
            <ArrowUpFromLine className="h-4 w-4" />
            <span>Withdraw</span>
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'transactions', label: 'Transactions' },
              { id: 'withdrawals', label: 'Withdrawals' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">This Month Deposits</span>
                    <span className="font-semibold text-green-600">₹0.00</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">This Month Withdrawals</span>
                    <span className="font-semibold text-red-600">₹0.00</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Contest Winnings</span>
                    <span className="font-semibold text-blue-600">₹0.00</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 border rounded-lg">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-700">Credit/Debit Cards</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border rounded-lg">
                    <Building className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-700">Net Banking</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border rounded-lg">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-700">UPI & Wallets</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'transactions' && (
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <WalletIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl">
                          {getTransactionIcon(transaction.transaction_type)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{transaction.description}</p>
                          <p className="text-sm text-gray-600">{formatDate(transaction.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}₹{Math.abs(transaction.amount).toFixed(2)}
                        </p>
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getTransactionTypeColor(transaction.transaction_type)
                        }`}>
                          {transaction.transaction_type.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'withdrawals' && (
          <Card>
            <CardHeader>
              <CardTitle>Withdrawal History</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowUpFromLine className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No withdrawals yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">
                          Withdrawal to {withdrawal.bank_account_number.slice(-4)}
                        </p>
                        <p className="text-sm text-gray-600">{formatDate(withdrawal.created_at)}</p>
                        <p className="text-sm text-gray-500">{withdrawal.account_holder_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">₹{withdrawal.amount.toFixed(2)}</p>
                        {getStatusBadge(withdrawal.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Deposit Modal */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Add Money to Wallet</h3>
                
                {/* Quick Amount Buttons */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDepositAmount(amount.toString())}
                      className="p-3 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors"
                    >
                      ₹{amount}
                    </button>
                  ))}
                </div>

                {/* Custom Amount Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Amount
                  </label>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Minimum ₹10"
                    min="10"
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    onClick={handleDeposit}
                    disabled={loading || !depositAmount || parseFloat(depositAmount) < 10}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {loading ? 'Processing...' : 'Add Money'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDepositModal(false);
                      setDepositAmount('');
                      setError('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Withdraw Money</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={withdrawalForm.amount}
                      onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Minimum ₹100"
                      min="100"
                      max={wallet.unutilized_amount}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available: ₹{wallet.unutilized_amount.toFixed(2)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.account_holder_name}
                      onChange={(e) => setWithdrawalForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter account holder name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.bank_account_number}
                      onChange={(e) => setWithdrawalForm(prev => ({ ...prev, bank_account_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter account number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      value={withdrawalForm.ifsc_code}
                      onChange={(e) => setWithdrawalForm(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter IFSC code"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <Button
                    onClick={handleWithdrawal}
                    disabled={loading || !withdrawalForm.amount || !withdrawalForm.bank_account_number || !withdrawalForm.ifsc_code || !withdrawalForm.account_holder_name}
                    className="flex-1"
                  >
                    {loading ? 'Processing...' : 'Withdraw'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawalForm({
                        amount: '',
                        bank_account_number: '',
                        ifsc_code: '',
                        account_holder_name: ''
                      });
                      setError('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;